import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { toast } from "sonner";

import { translate } from "@/constants/i18n";
import { queryClient } from "@/services/query-client";
import type { ProblemDetail, TokenPair } from "@/meta/types";
import { adoptTokens, authSnapshot, persistedTokens, useAuthStore } from "@/store/auth";
import { useLocaleStore } from "@/store/locale";

export const api = axios.create({
  // Lists go over as repeated keys (a=1&a=2), which is what FastAPI reads.
  paramsSerializer: { indexes: null },
  // The trailing slash comes off: a deployment that hands us the API root with
  // one would otherwise build `//api/v1/...`, which the server 404s rather than
  // folding — the doubled slash is a different path, not the same one twice.
  baseURL: `${(import.meta.env.VITE_API_GENESIS_URL ?? "").replace(/\/+$/, "")}/api/v1`,
  headers: { "Content-Type": "application/json" },
  // A minute before giving up. A search over tens of millions of members can
  // take a while and is worth waiting for; abandoning it at twenty seconds only
  // means asking again. The server is told when we do give up — see the
  // cancellation watcher on the session — so a wait nobody is having is not a
  // query nobody is having.
  timeout: 60_000,
});

/**
 * One request to a server that is not answering, not one per query on screen.
 *
 * A page holds several reads and fires them together — the shell asks for the
 * profile and the policy, a list asks for its rows and its lookups — so an API
 * that is down was met by the whole set at once, each waiting out its own
 * connection timeout, before anything could tell the person what had happened.
 * They cannot check on each other: they start in the same tick.
 *
 * So the first failure closes the door behind it. While it is shut, every
 * request is refused here without a socket being opened, with the same error
 * the real attempt would have produced — so callers cannot tell the difference
 * and none of them has to know this exists.
 *
 * It re-opens on its own after `DOWN_WINDOW`, and the first request through a
 * re-opened door is the only one that reaches the network — if it fails too, it
 * shuts the door again behind itself. The window is short because it is a burst
 * this is for, and a burst is simultaneous: five seconds is far longer than the
 * milliseconds between one page's reads, and far shorter than the time anyone
 * takes to press Try again, so a person retrying is never turned away by it.
 */
const DOWN_WINDOW = 5_000;

/** When the door shut. `0` is open. */
let downSince = 0;

const doorIsShut = () => downSince !== 0 && Date.now() - downSince < DOWN_WINDOW;

api.interceptors.request.use(async (config) => {
  // Refused before a socket is opened, and shaped exactly like the failure it
  // stands in for — no response, so `isUnreachable` and `errorMessage` read it
  // the same way they read the real thing.
  if (doorIsShut()) {
    throw new AxiosError("Cannot reach the server.", AxiosError.ERR_NETWORK, config);
  }

  // The token is renewed here, before it is used, rather than after the server
  // has refused it. Waiting for the 401 means every request in the first batch
  // after an access token expires is sent, refused, and sent again — three
  // round trips for a page's worth of reads, on the reload that follows any
  // quarter-hour spent reading a screen. It also means the SSE reader and
  // anything else outside these interceptors each discover the expiry on their
  // own. The 401 path below stays, because a token can be refused for reasons
  // its `exp` does not know about.
  if (
    renewEarly &&
    !isPublicAuthCall(config.url) &&
    expiresWithin(authSnapshot().accessToken, CLOCK_SKEW_MS)
  ) {
    // A refresh that cannot be done is not a reason to hold the request: it may
    // not need a token at all, and if it does the 401 path will say so.
    await refreshTokens().catch(() => undefined);
    // The rotation above re-measured the clock. A token minted seconds ago
    // that still reads as expiring even after that is beyond explaining, and
    // renewing early again would rotate once per request from here on.
    if (expiresWithin(authSnapshot().accessToken, CLOCK_SKEW_MS)) renewEarly = false;
  }

  const { accessToken } = authSnapshot();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  // A file goes as multipart, and only the browser can write the boundary. The
  // instance default above would otherwise stay on the request and the body
  // would be run through the JSON transform, which turns a FormData into `{}`
  // — the file silently gone, with a 200-shaped request to show for it.
  if (config.data instanceof FormData) config.headers.delete("Content-Type");
  // The backend writes its error messages in this language; "en" is its default.
  config.headers["Accept-Language"] = useLocaleStore.getState().locale;
  return config;
});

/**
 * A single refresh in flight, shared by every request that hits a 401 at once.
 * Without this, a page that fires five queries on mount would rotate the
 * refresh token five times — and the backend treats a replayed refresh token as
 * theft, which would sign the user out of everything.
 */
let refreshInFlight: Promise<TokenPair> | null = null;

/** How early a token is renewed, and the margin allowed on a disagreeing clock. */
const CLOCK_SKEW_MS = 60_000;

/**
 * How far this browser's clock is from the server's, in milliseconds.
 *
 * Renewing early means reading `exp` and comparing it against `Date.now()` —
 * the one clock in the exchange that nobody controls. A machine set half an
 * hour fast reads every token the server mints as already spent, so it renews
 * before each request, and each renewal rotates: a refresh token burned and a
 * session row written per request, for as long as the tab is open.
 *
 * The server hands over enough to measure the gap rather than fall foul of it.
 * A token pair carries the expiry twice — `exp` inside the token as a moment,
 * `expires_in` beside it as a duration — and the difference between the moment
 * it claims and the moment this clock makes of "now plus that duration" is the
 * error, near enough: the round trip is seconds against a margin of sixty.
 *
 * So every rotation re-measures, and `expiresWithin` reads the corrected time.
 * Zero until the first one, which is why a skewed clock still spends one
 * needless refresh per page load — and only one.
 */
let clockOffset = 0;

/** The server's idea of now, as far as this tab can tell. */
const serverNow = () => Date.now() + clockOffset;

/**
 * Re-measure the gap from a pair the server has just minted.
 *
 * Skipped without an `expires_in` to compare against — the adopted-from-
 * another-tab pair has no fresh measurement in it, only what was already
 * known, and calibrating from that would fold this tab's own error back in.
 */
function calibrateClock(tokens: TokenPair): void {
  if (!tokens.expires_in || tokens.expires_in <= 0) return;
  const claimed = expiryOf(tokens.access_token);
  if (typeof claimed !== "number") return;
  clockOffset = claimed - (Date.now() + tokens.expires_in * 1000);
}

/**
 * Whether early renewal is still worth attempting.
 *
 * The backstop behind the measurement above: if a token the server minted
 * seconds ago *still* reads as expiring once the clock has been calibrated
 * from that very response, nothing here can explain it, and renewing early
 * would rotate once per request forever. It switches itself off instead, and
 * the 401 path carries the work — slower by a round trip, and correct whatever
 * the clocks say, because the only judge of expiry in it is the server.
 */
let renewEarly = true;

/**
 * A refresh gets its own, shorter deadline than the minute a query is given.
 *
 * Every 401'd request is queued behind this one, so a refresh left hanging
 * holds the whole page open with nothing on screen to explain it. There is
 * also nothing here worth waiting out: it is one small round trip, and a slow
 * one is a server that is not going to answer.
 */
const REFRESH_TIMEOUT = 15_000;

/** The one name every tab takes before rotating, so only one of them does. */
const ROTATION_LOCK = "mrm.session.rotate";

/**
 * Whether a JWT is spent, or close enough that it will be by the time it lands.
 *
 * The payload is read without the signature being checked, which is all a
 * browser can do and all this needs: the claim is only being used to decide
 * whether to renew early, and the server is still the one that decides whether
 * a token is good. An unreadable token counts as expired — better a refresh
 * that was not needed than a request sent with something the server will not
 * take. A token with no `exp` at all does not, because nothing can be said
 * about it and refreshing on every single request is worse.
 */
function expiresWithin(token: string | null, margin: number): boolean {
  if (!token) return false;
  const expiry = expiryOf(token);
  if (expiry === null) return false;
  if (expiry === "unreadable") return true;
  return expiry - serverNow() <= margin;
}

/**
 * The `exp` claim as a timestamp, `null` when the token has none, and
 * `"unreadable"` when it is not a JWT this can make sense of at all.
 *
 * Three answers rather than two because the callers want different things from
 * the last case: a token nothing can be read out of should be renewed, but it
 * should not be reported as having a lifetime.
 */
function expiryOf(token: string): number | "unreadable" | null {
  const [, payload] = token.split(".");
  if (!payload) return "unreadable";
  try {
    // base64url, and `atob` only reads base64: the two differ in two characters
    // and in the padding, which `atob` requires and a JWT omits.
    const base64 = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const exp = (JSON.parse(atob(base64)) as { exp?: number }).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return "unreadable";
  }
}

/** What a pair adopted from another tab has left, for the `expires_in` field. */
function remainingSeconds(token: string | null): number {
  if (!token) return 0;
  const expiry = expiryOf(token);
  if (typeof expiry !== "number") return 0;
  return Math.max(0, Math.round((expiry - serverNow()) / 1000));
}

/**
 * Shared, so nothing rotates the refresh token twice.
 *
 * The single flight above is the whole point: the backend treats a replayed
 * refresh token as theft and revokes every session for that account, so two
 * callers refreshing at once would sign the person out of everything. The SSE
 * client cannot go through the interceptors — it is `fetch`, for a body read
 * as it arrives — so it comes here instead of refreshing on its own.
 */
export async function refreshSession(): Promise<TokenPair> {
  return refreshTokens();
}

/**
 * Hold the rotation lock across every tab, for as long as `run` takes.
 *
 * The single flight above is per tab, and a refresh token is single-use across
 * all of them: two tabs whose access tokens expire in the same minute will each
 * reach for the pair they hold, and the second one to arrive is presenting a
 * token the first has already spent. The server reads that as theft.
 *
 * Web Locks is the only thing a browser offers that is actually mutual across
 * tabs. Where it is missing — jsdom under test, an older browser — `run` is
 * called straight through: the caller re-reads storage inside it either way, so
 * the window left open is the few milliseconds between that read and the
 * request, rather than the minutes between two tabs' expiries. The server's own
 * grace window covers what is left.
 */
async function withRotationLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks) return run();
  return locks.request(ROTATION_LOCK, run);
}

/** A refusal, as opposed to never having got an answer. */
function isRefused(error: unknown): boolean {
  const status = statusOf(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * End the session here and now, wherever the dead end was found.
 *
 * Clearing the store *is* the redirect: the route gates subscribe to it, so the
 * sign-in screen replaces whatever is on screen in the same tick rather than
 * waiting for the person to navigate somewhere and discover it.
 *
 * Guarded on there being a session left to end, because a page fires several
 * requests at once and they all come back 401 together: the first one through
 * empties the store, and the rest find it empty and say nothing. Without that
 * the person is told five times.
 */
function endSession(reason: "session.expired" | "session.invalid" = "session.expired") {
  const { accessToken, refreshToken } = authSnapshot();
  if (!accessToken && !refreshToken) return;
  useAuthStore.getState().signOut();
  toast.info(translate(useLocaleStore.getState().locale, reason), {
    id: "session-expired",
  });
}

/**
 * Rotate the refresh token, once, for whoever in this tab or any other needs it.
 *
 * The result is written to the store before it is handed back, so a caller
 * cannot forget to — two of them did, and each had to be kept in step with the
 * other by hand.
 */
async function refreshTokens(): Promise<TokenPair> {
  refreshInFlight ??= rotate().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function rotate(): Promise<TokenPair> {
  const held = authSnapshot().refreshToken;

  return withRotationLock(async () => {
    // Read again, now that this tab is the only one rotating. Another tab may
    // have held the lock for its own refresh and finished while this one
    // waited, in which case the pair in storage is fresh and the token this
    // call set out with has already been spent. Presenting it is what the
    // server answers by revoking every session for the account.
    //
    // Adopted only while the access token beside it is still worth sending.
    // A tab that rotated an hour ago left a live refresh token next to an
    // access token that has since expired, and handing that back is a caller
    // sent to the server with something it will refuse — on its one retry,
    // which then ends the session. When it is stale the rotation goes ahead
    // below, on the shared refresh token rather than the spent one held here.
    const shared = persistedTokens();
    const rotatedElsewhere = shared.refreshToken === held ? null : shared.refreshToken;
    if (
      rotatedElsewhere &&
      shared.accessToken &&
      !expiresWithin(shared.accessToken, CLOCK_SKEW_MS)
    ) {
      adoptTokens(shared.accessToken, rotatedElsewhere);
      return {
        access_token: shared.accessToken,
        refresh_token: rotatedElsewhere,
        token_type: "bearer",
        expires_in: remainingSeconds(shared.accessToken),
      };
    }

    const refreshToken = shared.refreshToken ?? held;
    if (!refreshToken) {
      // Nothing to rotate. Whatever is on screen is not a session.
      endSession();
      throw new AxiosError("No refresh token", AxiosError.ERR_BAD_REQUEST);
    }

    try {
      const { data } = await axios.post<TokenPair>(
        `${api.defaults.baseURL}/auth/refresh`,
        { refresh_token: refreshToken },
        { timeout: REFRESH_TIMEOUT },
      );
      // Measured before the pair is handed on, so the very next `expiresWithin`
      // reads the corrected clock rather than the one that sent us here.
      calibrateClock(data);
      // Written here, inside the lock, so the next tab through reads it.
      useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
      return data;
    } catch (error: unknown) {
      // A *refusal* is the end of the session: the token is spent, revoked, or
      // was never good, and there is no third chance to take. Anything else —
      // a timeout, a dropped connection, the backend restarting, a 502 from
      // the gateway — says nothing about the token, and signing the person out
      // over it loses their work to a hiccup they would not otherwise have
      // noticed. The pair is still good when the server comes back.
      if (isRefused(error)) endSession();
      throw error;
    }
  });
}

/**
 * The precondition an update or delete carries: the record as it was last read.
 *
 * The server compares it with the row as it stands and refuses a save over
 * someone else's with 412 — see `stale` below — so two people editing one
 * record cannot silently overwrite each other. Pass the record the screen
 * holds; a record without a timestamp sends nothing, and the server answers
 * 428 so the gap is noticed rather than waved through.
 *
 * It rides as a query parameter rather than the `If-Match` header it names:
 * the header is not CORS-safelisted, so the browser asks permission for it in
 * the preflight, and the preview gateway answers that preflight itself with a
 * fixed allow-list that does not name `If-Match`. The server reads either.
 */
export function ifMatch(record: { updated_at?: string | null | undefined } | null | undefined) {
  return record?.updated_at ? { params: { if_match: record.updated_at } } : {};
}

/** 412: the record moved under the person. 409 `record_busy`: somebody is saving it right now. */
function isStale(error: AxiosError<ProblemDetail>) {
  return error.response?.status === 412;
}
function isBusy(error: AxiosError<ProblemDetail>) {
  return error.response?.status === 409 && error.response.data?.type?.endsWith("#record_busy");
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/**
 * The endpoints a 401 is an *answer* from, not a session ending.
 *
 * Signing in with the wrong password is a 401, and so is a spent reset link:
 * neither is a session to end, and both are reported by the screen that asked.
 * `/auth/logout` is here because it is sent while signing out anyway — there is
 * nothing left to rescue and a refresh would only spend a token on the way out.
 *
 * Spelled out rather than tested as `/auth/`: that prefix also covers the
 * signed-in reads — `/auth/sessions` and the rest — and a 401 on those was
 * being skipped over, neither refreshed nor signed out, which is a page of
 * empty panels and no explanation.
 */
const PUBLIC_AUTH_PATHS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/password-reset-request",
  "/auth/password-change",
];

const isPublicAuthCall = (url: string | undefined) =>
  PUBLIC_AUTH_PATHS.some((path) => url?.startsWith(path));

api.interceptors.response.use(
  (response) => {
    // Anything at all came back, so the server is there.
    downSince = 0;
    return response;
  },
  async (error: AxiosError<ProblemDetail>) => {
    const config = error.config as RetriableConfig | undefined;
    const isAuthCall = isPublicAuthCall(config?.url);

    // An error the server *sent* means it is up and had something to say, even
    // if that thing was a 500. Only silence shuts the door — and a request this
    // interceptor refused is already behind it, so it does not reset the clock.
    if (isUnreachable(error)) {
      if (!doorIsShut()) downSince = Date.now();
    } else if (error.response) {
      downSince = 0;
    }

    // An administrator forced a password change mid-session: the API now walls
    // off everything but the change, so go there. The store is persisted, so a
    // full navigation keeps the session.
    if (
      error.response?.status === 403 &&
      error.response.data?.type?.endsWith("#password_change_required") &&
      !window.location.pathname.startsWith("/change-password")
    ) {
      window.location.assign("/change-password");
    }

    // Somebody else got there first. Everything held is refetched, so the
    // screen shows what is actually there and the person edits from that; the
    // mutation still rejects, so the form keeps their draft on screen to redo.
    if (isStale(error)) {
      toast.error(translate(useLocaleStore.getState().locale, "common.staleRecord"));
      void queryClient.invalidateQueries();
    } else if (isBusy(error)) {
      toast.error(translate(useLocaleStore.getState().locale, "common.recordBusy"));
    }

    // A 401 is the session being refused, and the only thing that can rescue
    // one is a refresh that works. Anything else here is a dead end, and a dead
    // end ends the session on the spot rather than leaving the person on a
    // screen that looks signed in and answers nothing.
    if (error.response?.status === 401 && !isAuthCall) {
      // The session itself is gone — signed out elsewhere, taken over by a
      // sign-in on another device, or idle past the timeout. A refresh cannot
      // rescue that: the refresh token belongs to the same dead session and
      // asking would spend it to be told the same thing. End here, with the
      // message that says what actually happened.
      if (error.response.data?.type?.endsWith("#session_invalid")) {
        endSession("session.invalid");
      } else if (config && !config._retried) {
        config._retried = true;
        try {
          const tokens = await refreshTokens();
          config.headers.Authorization = `Bearer ${tokens.access_token}`;
          return api.request(config);
        } catch {
          // A refusal has already ended the session in `rotate`. Anything else
          // left it alone on purpose, and the original 401 below is the honest
          // thing to hand the caller: this one request failed, the session did
          // not. Retrying it is the person's to do.
        }
      } else {
        // Refused again *with a brand new access token*, or refused with no
        // request to retry. The token is not the problem: the account is
        // disabled, the session was revoked, or the server no longer accepts
        // this principal at all. Refreshing again would only spend the refresh
        // token to be told the same thing.
        endSession();
      }
    }

    return Promise.reject(error);
  },
);

/** Pull a human-readable message out of a problem+json response. */
/** The HTTP status behind a failure, or undefined when nothing answered. */
export function statusOf(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

/**
 * No answer at all — the server is down, restarting, or the network is gone.
 *
 * Different in kind from an error the server *sent*: that one has something to
 * say for itself and is usually about the request, while this one is about
 * everything and clears on its own. The two deserve different screens, so the
 * test lives here rather than being spelled out at each of them.
 *
 * A request cancelled on this side has no answer either, and is not one: paging
 * quickly aborts each page's read for the next, and counting those as silence
 * shut the door on the page the person actually landed on.
 */
export function isUnreachable(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && !axios.isCancel(error);
}

export function errorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    const problem = error.response?.data;
    if (problem?.errors?.length) {
      // "Value error, Must be at least 18 years old." — the prefix is
      // Pydantic's, stamped on anything a validator raises, and the API keeps
      // it so the wire shape stays the one FastAPI documents. It is not a
      // sentence anybody should be shown, so it comes off here, at the last
      // moment, rather than being dropped from the response everything else
      // parses.
      return problem.errors.map((item) => item.msg.replace(/^Value error, /, "")).join(". ");
    }
    if (problem?.detail) return problem.detail;
    if (typeof problem?.title === "string" && problem.title) return problem.title;
    if (isUnreachable(error)) return "Cannot reach the server.";
    // Every problem this API raises carries a detail, so a response with none
    // came from something in front of it — a gateway's own page, a proxy
    // timeout. The code is then the only fact there is, and a screen that
    // keeps it to itself leaves nothing to report.
    const status = error.response?.status;
    if (status) return `${fallback} (HTTP ${status})`;
  }
  return fallback;
}
